import { useEffect } from "react";
import { Navigate } from "react-router-dom";
import { useToast } from "@promptgatewaysadmin/ui";
import { useSubscriptionState } from "../../queries/hooks.js";
import type { SubscriptionStatus } from "../../api/normalise.js";

/**
 * Where a signed-in user with a verified account and an org lands from /signup
 * (RedirectIfAuthenticated, `signup` mode). Never /checkout:
 *
 *   trialing / locked / canceled   -> /subscription  (not paying: the plan choice lives there)
 *   active / past_due / incomplete -> /              (paying: nothing to buy)
 *   unknown (error)                -> /
 *
 * A member is routed the same way and RequireAdmin takes it from there (toast, then "/").
 *
 * This only ever routes a visitor AWAY FROM /signup, and a locked org goes TO the screen where
 * it pays. It is not a lockout, which is why it may read status when the guards may not.
 */
export function ExistingAccountRedirect({ email }: { email: string }) {
  const sub = useSubscriptionState();
  const toast = useToast();
  const settled = !sub.isPending;
  const to = sub.data && NOT_PAYING.has(sub.data.status) ? "/subscription" : "/";

  // Once per redirect, the same way RequireAdmin fires its toast.
  useEffect(() => {
    if (settled) {
      toast.show(`You already have an account. To use the extension, sign in there with ${email}.`);
    }
  }, [settled, email, toast]);

  // Nothing while the status loads: a guess would be a redirect we then take back.
  if (!settled) return null;
  return <Navigate to={to} replace />;
}

const NOT_PAYING: ReadonlySet<SubscriptionStatus> = new Set(["trialing", "locked", "canceled"]);
